import { memo, useCallback, useMemo, useState } from "react";
import { Entity } from "@common/types/entity";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { getScheduleEntityLock } from "@common/helpers/schedule-transaction-field-lock";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { useEntities, useEntitiesById } from "@hooks/useEntities";
import { Picker } from "@design-system/Picker/Picker.lib";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { PickerEntityItem } from "@components/entity/PickerEntityItem";
import { useSimilarTransactionsNewValue } from "./SimilarTransactionsNewValueContext";

export const EntityNewValueCell: React.FC<{
    transaction: TransactionListItem;
    newEntity: Entity;
}> = memo(({ transaction, newEntity }) => {
    const { setNewValue } = useSimilarTransactionsNewValue();
    const allEntities = useEntities({ excludeMockEntities: true });
    const entitiesById = useEntitiesById({ excludeMockEntities: true });
    const [isOpen, setIsOpen] = useState(false);
    const { isLocked, tooltip: lockTooltip } = getScheduleEntityLock({
        schedule: transaction.schedule,
    });
    const entity = isLocked ? transaction.entity : newEntity;
    const transactionId = transaction.id;

    const handleEntityChange = useCallback(
        (entityId: number | null) => {
            if (entityId === null || entityId === entity.id) {
                setIsOpen(false);
                return;
            }
            const selected = entitiesById.get(entityId);
            if (selected) {
                setNewValue(transactionId, selected);
                setIsOpen(false);
            }
        },
        [entity.id, entitiesById, transactionId, setNewValue],
    );

    const hasMultipleEntities = allEntities.length > 1;

    const trigger = useMemo(
        () =>
            hasMultipleEntities ? (
                <Picker.CellContent
                    readOnly={isLocked}
                    buttonProps={{ tooltip: lockTooltip }}
                    addText="Add entity"
                    value={
                        entity ? (
                            <EntityAvatar entity={entity} size="xs" />
                        ) : undefined
                    }
                    aria-label={entity?.name ?? undefined}
                />
            ) : null,
        [hasMultipleEntities, entity, isLocked, lockTooltip],
    );

    if (!hasMultipleEntities) {
        return (
            <ViewTableCell>
                <EditableCellContent
                    readonly
                    truncationFullText={entity.name ?? undefined}
                >
                    <EntityAvatar entity={entity} size="xs" />
                </EditableCellContent>
            </ViewTableCell>
        );
    }

    return (
        <ViewTableCell captureClicks>
            <Picker.Select
                value={entity.id}
                onValueChange={handleEntityChange}
                open={isOpen}
                onOpenChange={setIsOpen}
                chevron="none"
                disabled={isLocked}
            >
                {trigger}
                <Picker.Popup>
                    <Picker.List>
                        {allEntities.map((e) => (
                            <PickerEntityItem
                                key={e.id}
                                value={e.id}
                                entity={e}
                            />
                        ))}
                    </Picker.List>
                </Picker.Popup>
            </Picker.Select>
        </ViewTableCell>
    );
});
EntityNewValueCell.displayName = "EntityNewValueCell";
